import React, { useContext, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { AppContext } from '@state/context'
import { closePanel } from '@state/actions/panelsActions'
import { uploadImage } from '@helpers/api'
import { useCreateExam } from '@hooks/mutations/useCreateExam'
import {
  PANELS,
  DIVIDER_SIZE,
  BUTTON_SIZE,
  HEADING_TYPE,
  BUTTON_TYPE,
  TEXT_SIZES,
  EXAM_TYPES,
  EXAM_QUESTION_ORDER_TYPES,
  EXAM_DISPLAY_TYPES,
} from '@helpers/constants'
import Button from '@components/Button'
import Heading from '@components/Heading'
import GridColumns from '@components/GridColumns'
import Divider from '@components/Divider'
import Form from '@components/Form'
import ButtonWithLoader from '@components/ButtonWithLoader'

const CreateExamPanel = () => {
  const [state, dispatch] = useContext(AppContext)
  const {
    app: { userId, currentSection },
  } = state

  const [selectedType, setSelectedType] = useState(EXAM_TYPES.QUIZ.value)

  const selectedTypeLabel =
    Object.values(EXAM_TYPES).find(
      (examType) => examType.value === selectedType
    )?.label || ''

  const { post, isPosting } = useCreateExam()

  const { register, handleSubmit, errors, control, formState } = useForm({
    mode: 'onChange',
    defaultValues: {
      type: EXAM_TYPES.QUIZ.value,
      questionOrderType: EXAM_QUESTION_ORDER_TYPES.SEQUENTIAL.value,
      displayType: EXAM_DISPLAY_TYPES.PANEL.value,
    },
  })
  const { isValid } = formState

  const onSubmit = async (data) => {
    let fileName = ''

    if (data.image && data.image.length > 0) {
      // We have image, upload it!
      try {
        fileName = await uploadImage(data.image[0])
      } catch (error) {
        console.log(error)
        // show toast here?
      }
    }

    const postData = {
      name: data.name,
      type: parseInt(selectedType),
      connectSections: [currentSection.id],
      creator: userId,
      image: fileName,
      description: data.description,
      additionalDescriptionOne: data.additionalDescriptionOne,
      additionalDescriptionTwo: data.additionalDescriptionTwo,
    }

    if (selectedType === EXAM_TYPES.QUIZ.value) {
      postData['passingPercentage'] = parseInt(data.passingPercentage)
      postData['maxAttempts'] = parseInt(data.maxAttempts)
      postData['questionOrderType'] = parseInt(data.questionOrderType)
      postData['showAllRightAnswers'] =
        data.showAllRightAnswers === 'yes' ? true : false
      postData['answerDisplayCount'] = parseInt(data.answerDisplayCount)
      postData['displayType'] = parseInt(data.displayType)
    }

    const res = await post(postData)

    if (res.success) {
      dispatch(closePanel(PANELS.CREATE_EXAM))
    }
  }

  return (
    <>
      <div className='flex justify-end'>
        <Button
          handleClick={() => {
            dispatch(closePanel(PANELS.CREATE_EXAM))
          }}
          size={BUTTON_SIZE.NO_PADDING}
          icon={'Close'}
        ></Button>
      </div>

      <Heading headingLevel={HEADING_TYPE.H2} size={TEXT_SIZES.XLARGE}>
        Skapa ett test {selectedTypeLabel ? `- ${selectedTypeLabel}` : ''}
      </Heading>

      <Divider size={DIVIDER_SIZE.SMALL} />

      <Heading headingLevel={HEADING_TYPE.H3}>Välj typ</Heading>
      {Object.values(EXAM_TYPES).map((examType, index) => {
        const { label, value } = examType
        return (
          <Button
            key={`examType-${index}`}
            marginRight
            type={
              selectedType === value
                ? BUTTON_TYPE.PRIMARY
                : BUTTON_TYPE.SECONDARY
            }
            handleClick={() => {
              setSelectedType(value)
            }}
          >
            {label}
          </Button>
        )
      })}

      <Divider size={DIVIDER_SIZE.SMALL} />

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.Label htmlFor='name'>Namnge testet*</Form.Label>
        <Form.Input
          type={'text'}
          name='name'
          id='name'
          ref={register({ required: true })}
          placeholder='Titel'
        />
        {errors.name && <Form.ErrorMessage>Titel behövs</Form.ErrorMessage>}

        <Divider size={DIVIDER_SIZE.SMALL} />

        <Form.Label htmlFor='image'>Välj en bild</Form.Label>
        <Form.ImageUpload name='image' id='image' ref={register()} />

        <Divider size={DIVIDER_SIZE.SMALL} />

        <Form.Label htmlFor='description'>Inledande text</Form.Label>
        <Controller
          control={control}
          name='description'
          render={(
            { onChange, onBlur, value, name, ref },
            { invalid, isTouched, isDirty }
          ) => (
            <Form.Editor
              handleChange={(value) => onChange(value)}
              elementId={`create-exam-description`}
              // placeholder='Beskrivning'
            />
          )}
        />

        <Divider size={DIVIDER_SIZE.SMALL} />

        <Form.Label htmlFor='additionalDescriptionOne'>
          &quot;Uppgift&quot;-text
        </Form.Label>
        <Controller
          control={control}
          name='additionalDescriptionOne'
          render={(
            { onChange, onBlur, value, name, ref },
            { invalid, isTouched, isDirty }
          ) => (
            <Form.Editor
              handleChange={(value) => onChange(value)}
              elementId={`create-exam-additional-description-one`}
              // placeholder='Beskrivning'
            />
          )}
        />

        <Divider size={DIVIDER_SIZE.SMALL} />

        <Form.Label htmlFor='additionalDescriptionTwo'>
          &quot;Bra att veta&quot;-text
        </Form.Label>
        <Controller
          control={control}
          name='additionalDescriptionTwo'
          render={(
            { onChange, onBlur, value, name, ref },
            { invalid, isTouched, isDirty }
          ) => (
            <Form.Editor
              handleChange={(value) => onChange(value)}
              elementId={`create-exam-additional-description-two`}
              // placeholder='Beskrivning'
            />
          )}
        />

        {selectedType === EXAM_TYPES.QUIZ.value && (
          <>
            <Divider size={DIVIDER_SIZE.SMALL} />

            <GridColumns cols={2}>
              <div>
                <Form.Label htmlFor='passingPercentage'>Procent*</Form.Label>
                <Form.Input
                  type={'number'}
                  name='passingPercentage'
                  id='passingPercentage'
                  ref={register({ required: true })}
                />
                {errors.passingPercentage && (
                  <Form.ErrorMessage>Procent behövs</Form.ErrorMessage>
                )}
              </div>
              <div>
                <Form.Label htmlFor='maxAttempts'>Antal försök*</Form.Label>
                <Form.Input
                  type={'number'}
                  name='maxAttempts'
                  id='maxAttempts'
                  ref={register({
                    required: true,
                    min: {
                      value: 1,
                      message: 'Antal försök får inte vara mindre än 1',
                    },
                  })}
                />
                {errors.maxAttempts && (
                  <Form.ErrorMessage>
                    {errors.maxAttempts.message ?? 'Antal försök behövs'}
                  </Form.ErrorMessage>
                )}
              </div>

              <div>
                <Form.Label htmlFor='questionOrderType'>
                  Frågeordning
                </Form.Label>
                <Form.Select
                  name='questionOrderType'
                  id='questionOrderType'
                  ref={register({ required: true })}
                  options={Object.values(EXAM_QUESTION_ORDER_TYPES).map(
                    (orderType, index) => {
                      const { label, value } = orderType
                      return { value: value, name: label }
                    }
                  )}
                />
                {errors.questionOrderType && (
                  <Form.ErrorMessage>Frågeordning behövs</Form.ErrorMessage>
                )}
              </div>
              <div>
                <Form.Label htmlFor='answerDisplayCount'>
                  Antal alternativ
                </Form.Label>
                <Form.Select
                  name='answerDisplayCount'
                  id='answerDisplayCount'
                  ref={register({ required: true })}
                  options={[
                    { value: '3', name: '3' },
                    { value: '4', name: '4' },
                    { value: '5', name: '5' },
                  ]}
                />
                {errors.answerDisplayCount && (
                  <Form.ErrorMessage>Antal alternativ behövs</Form.ErrorMessage>
                )}
              </div>

              <div>
                <Form.Label htmlFor='displayType'>Visningsläge</Form.Label>
                <Form.Select
                  name='displayType'
                  id='displayType'
                  ref={register({ required: true })}
                  options={Object.values(EXAM_DISPLAY_TYPES).map(
                    (orderType, index) => {
                      const { label, value } = orderType
                      return { value: value, name: label }
                    }
                  )}
                />
                {errors.displayType && (
                  <Form.ErrorMessage>Visningsläge behövs</Form.ErrorMessage>
                )}
              </div>
              <div>
                <Form.Label htmlFor='showAllRightAnswers'>
                  Visa alla rätta svar
                </Form.Label>
                <Form.Checkbox
                  id='showAllRightAnswers'
                  label='Ja'
                  value={'yes'}
                  name='showAllRightAnswers'
                  ref={register()}
                />
              </div>
            </GridColumns>
          </>
        )}

        <Divider size={DIVIDER_SIZE.BASE} />

        <ButtonWithLoader
          type={BUTTON_TYPE.PRIMARY}
          isLoading={isPosting}
          disabledText={'Skapa test'}
          enabledText={'Skapa test'}
          disabled={!isValid || isPosting}
        />

        <Divider size={DIVIDER_SIZE.SMALL} />
      </Form>
    </>
  )
}

export default CreateExamPanel
